import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useState} from 'react';

import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import {MAX_PROCEDURE_RUN_DESCRIPTION_LENGTH} from 'procedures/design/ProcedureDesignConstants';
import * as ProcedureRunDAO from 'procedures/network/ProcedureRunDAO';
import * as ProcedureRunPlanDAO from 'procedures/network/procedureRunPlanDAO';
import {ProcedureRunTab} from 'studies/redux/ProcedureRunPlanUIReduxActions';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';
import StudyDescriptionEditor from 'studies/workspace/StudyDescriptionEditor';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import * as Colors from 'ui-library/Colors';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import {Tab, TabContainer, TabContainerProps, TabProps} from 'ui-library/TabContainer';
import * as DateUtil from 'util/DateUtil';

import {StudyOverviewTabContentsProps} from '../../../utils/TypedStudyTypes';
import PlanPill, {PlanPillVariant} from './PlanPill';
import {StudyOverviewContext} from './StudyOverviewContext';

interface BaseStudyExecutionProps extends StudyOverviewTabContentsProps {
  dataTestAttrs?: DataTestAttrs;
  executionContainerStyling?: React.CSSProperties;
  headerBreadcrumb?: JSX.Element;
  headerButtons?: {buttonsStyling: React.CSSProperties; buttons: JSX.Element};
  headerSectionContainerStyling?: React.CSSProperties;
  procedureRunId?: string;
  procedureRunDescription?: string;
  procedureRunPlanId?: string;
  procedureRunOrPlanCreatedAt?: string;
  procedureRunPlanDescription?: string;
  title?: string;
}

/** @package */
export type StudyExecutionHeaderTabsProps<T extends string> =
  | {
      headerTabContainerProps: Omit<TabContainerProps<T>, 'children'>;
      headerTabsProps: Array<Omit<TabProps<T>, 'children'>>;
    }
  | {headerTabContainerProps?: never; headerTabsProps?: never};
type StudyExecutionProps<T extends string> = BaseStudyExecutionProps & StudyExecutionHeaderTabsProps<T>;

export default function StudyExecution<T extends string>(props: StudyExecutionProps<T>): JSX.Element {
  const {isProcedureRunDesignInEditMode, currentProcedureRunTab, setIsProcedureRunDescriptionInEditMode} =
    StudyOverviewContext.useContext();
  const [procedureRunDescription, setProcedureRunDescription] = useState(props.procedureRunDescription ?? '');
  const [procedureRunPlanDescription, setProcedureRunPlanDescription] = useState(
    props.procedureRunPlanDescription ?? ''
  );
  const isDesignTabActive = props.procedureRunId != null && currentProcedureRunTab === ProcedureRunTab.DESIGN;
  const shouldRenderRunDescription = !(isProcedureRunDesignInEditMode && isDesignTabActive);

  const handleDescriptionEditFocusChange = useCallback(
    (isBeingEdited: boolean) => {
      setIsProcedureRunDescriptionInEditMode(isBeingEdited);
    },
    [setIsProcedureRunDescriptionInEditMode]
  );

  useEffect(() => {
    setProcedureRunDescription(props.procedureRunDescription ?? '');
  }, [props.procedureRunDescription, props.procedureRunId]);

  useEffect(() => {
    setProcedureRunPlanDescription(props.procedureRunPlanDescription ?? '');
  }, [props.procedureRunPlanDescription, props.procedureRunPlanId]);

  async function handleProcedureRunDescriptionChange(updatedDescription: string | number): Promise<void> {
    const description = updatedDescription as string;
    setProcedureRunDescription(description);
    await ProcedureRunDAO.updateProcedureRun(
      {
        id: props.procedureRunId!,
        description,
      },
      {supportsAsync: false}
    );
    props.refetch();
  }

  async function handleProcedureRunPlanDescriptionChange(updatedDescription: string | number): Promise<void> {
    const description = updatedDescription as string;
    setProcedureRunPlanDescription(description);
    await ProcedureRunPlanDAO.updateProcedureRunPlan({
      id: props.procedureRunPlanId!,
      description,
      studyId: props.study.id,
    });
    props.refetch();
  }

  const createdAtTimestamp =
    props.procedureRunOrPlanCreatedAt != null
      ? `Created at ${DateUtil.readableTime(props.procedureRunOrPlanCreatedAt, {showTime: true})}`
      : null;

  const descriptionEditor =
    props.procedureRunId != null && shouldRenderRunDescription ? (
      <EditableTextField
        requestChange={handleProcedureRunDescriptionChange}
        isDisabled={false} // TODO (BNCH-159710): Check procedure run permissions
        emptyValueAllowed={true}
        value={procedureRunDescription}
        maxLength={MAX_PROCEDURE_RUN_DESCRIPTION_LENGTH}
        type={TextFieldType.TEXTAREA}
        autoSize={true}
        textPlaceholder={`Add ${getProcedureRunUIStringForStudyType(props.study.studySchema.studyType)} description`}
        onIsBeingEditedChanged={handleDescriptionEditFocusChange}
      />
    ) : props.procedureRunPlanId != null ? (
      <EditableTextField
        requestChange={handleProcedureRunPlanDescriptionChange}
        // Procedure run plan's auth parent is the study
        isDisabled={!props.isEditable}
        emptyValueAllowed={true}
        value={procedureRunPlanDescription}
        maxLength={MAX_PROCEDURE_RUN_DESCRIPTION_LENGTH}
        type={TextFieldType.TEXTAREA}
        autoSize={true}
        textPlaceholder={`Add ${getProcedureRunUIStringForStudyType(props.study.studySchema.studyType)} plan description`}
      />
    ) : props.procedureRunId == null ? (
      <StudyDescriptionEditor study={props.study} refetch={props.refetch} isWritable={props.isEditable} />
    ) : null;

  return (
    <div className={css(styles.studyExecutionContainer)} {...getTestProps(props.dataTestAttrs)}>
      <div style={props.headerSectionContainerStyling}>
        <div className={css(styles.headerSection)}>
          {props.headerBreadcrumb && <div className={css(styles.breadcrumbs)}>{props.headerBreadcrumb}</div>}
          <div className={css(styles.headerTitleAndButtons)}>
            <div className={css(styles.title)}>
              {props.title ?? props.study.name}
              {props.procedureRunPlanId && (
                <PlanPill className={css(styles.planLabel)} variant={PlanPillVariant.HEADER} />
              )}
            </div>
            <div className={css(styles.headerActions)}>
              {createdAtTimestamp != null && (
                <div className={css(styles.createdAt)}>{createdAtTimestamp}</div>
              )}
              <div
                className={props.headerButtons ? undefined : css(styles.headerButtons)}
                style={props.headerButtons?.buttonsStyling}
              >
                {props.headerButtons?.buttons}
              </div>
            </div>
          </div>
          <ArchiveRecordLabel
            item={{name: props.study.name, id: props.study.id, archiveRecord: props.study.archiveRecord}}
          />
          {descriptionEditor != null && <div className={css(styles.description)}>{descriptionEditor}</div>}
        </div>
      </div>
      {props.headerTabContainerProps && (
        <TabContainer {...props.headerTabContainerProps}>
          {props.headerTabsProps.map((headerTabProps) => (
            <Tab key={headerTabProps.tabKey} {...headerTabProps}>
              {props.execution}
            </Tab>
          ))}
        </TabContainer>
      )}
      {!props.headerTabContainerProps && (
        <div className={css(styles.execution)} style={props.executionContainerStyling}>
          {props.execution}
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  breadcrumbs: {
    marginBottom: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  headerTitleAndButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
  },
  studyExecutionContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    paddingTop: 12,
  },
  title: {
    fontSize: '26px',
    lineHeight: '26px',
    fontWeight: 'bolder',
    display: 'flex',
    alignItems: 'center',
  },
  headerButtons: {
    flex: 'none',
    display: 'flex',
    marginLeft: 'auto',
    padding: 20,
    marginRight: '40px',
    gap: 18,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginLeft: 'auto',
    maxWidth: '100%',
  },
  createdAt: {
    color: Colors.GRAY_DARKER,
    whiteSpace: 'nowrap',
  },
  headerSection: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.WHITE,
    flex: '0 0 auto',
  },
  description: {
    marginBottom: 12,
    marginTop: 12,
  },
  execution: {
    backgroundColor: Color.GRAY_100,
    height: '100%',
    maxHeight: '100%',
  },
  planLabel: {
    marginLeft: '8px',
    marginRight: 'auto',
  },
});
